import { Freight } from './components/freight/Freight';
import styled from '@emotion/styled';
import React, { useEffect, useMemo, useRef } from 'react';
import { Price, PriceLabel, Prices } from './components/Price';
import { PositionSplash, Splash, StyledSplash } from './components/Splash';
import { Rating } from './components/Rating';
import {
    Notifications,
    NotificationWrap,
    Label as NotificationLabel,
} from './components/Notifications';
import { CTA, CTAWrapper, FixedCTAWrapper } from './components/CTA';
import { InfoWrapper, TopInfo } from './components/TopInfo';
import { Favourite, FavWrapper } from './components/Favourite';
import { TCampaignLifeCycle, TProductDetails } from './ProductCard.definition';
import { theme } from '~/theme';
import { useTranslation } from '~/shared/utils/translation';
import { TranslationKey } from '~/lib/data-contract/auto-generated/translation-model';
import Flex from '../Flex/Flex';
import { StyledAspectRatio } from '../AspectRatio/styled';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { MobilePDPCarousel } from '~/features/product-details-page/components/MobilePDPCarousel';
import { TStoreInfo, TVariantSummary } from '~/features/product-details-page/pdp.definitions';
import { PDPVariants } from '~/features/product-details-page/components/PDPVariants/PDPVariants';
import Spacer from '~/theme/design-system/components/Spacer';
import { useEvents } from '~/shared/hooks/useEvents/useEvents';
import { useFrame } from '~/shared/utils';
import { useMenuScroll } from '~/templates/pages/hooks/useMenuScroll';
import { ProductEventData } from '~/shared/hooks/useEvents/models';
import { breakpoints, breakpointsRaw } from '~/theme/breakpoints';
import { CampaignLifeCycle } from './components/CampaignLifeCycle';
import { useBasket } from '~/features/basket/hooks';
import { ProductPromotionTypeRedirect } from '~/features/productList/PLPModule.definitions';
import { NotifyWhenBack } from './components/notifyWhenBack/NotifyWhenBack';
import Personalization from './components/SpiffPersonalization/SpiffPersonalization';
import useProductPersonalization from '~/services/personalization/useProductPersonalization';
import { CampaignRedirecter } from './components/campaignRedirecter/CampaignRedirecter';
import { useProductLabels } from '~/shared/hooks/useProductLabels';

/**
 * Other components are relying on this `extra` type declariation.
 * This should probably just be renamed in the files importing it.
 */
export type ProductCardProps = TProductDetails & { beforePrice?: number };

const Wrapper = styled(Flex)(() => ({
    height: '100%',
    boxShadow: theme?.shadows.productCard,
    [`${StyledAspectRatio}`]: {
        background: theme.colors.brownUltraLight,
    },
    img: {
        mixBlendMode: 'multiply',
    },
}));

const Details = styled.div<{ paddingTop?: boolean }>(({ paddingTop }) => ({
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    flexDirection: 'column',
    padding: `${theme.space[paddingTop ? 4 : 3]} ${theme.space[4]} ${theme.space[4]}`,
    textAlign: 'center',
    color: theme.colors.black,
    [breakpoints.xs]: {
        padding: `${theme.space[3]} ${theme.space[3]} ${theme.space[3]}`,
    },
}));

const StyledOutOfStock = styled.div(() => ({
    fontSize: theme.fontSizes['xs'],
    marginTop: theme.space['2'],
    textAlign: 'right',
    [breakpoints.md]: {
        fontSize: theme.fontSizes['sm'],
        marginBottom: theme.space['2'],
        textAlign: 'left',
    },
}));

const Name = styled.h1(() => ({
    ...theme.mixins.useTextStyle('productName'),
    marginTop: theme.space[2],
    [breakpoints.md]: {
        marginTop: 0,
    },
}));

const Label = styled.span(() => ({
    display: 'block',
    ...theme.mixins.useTextStyle('productInfo'),
    fontWeight: theme.mixins.useFontWeight('Medium'),
    marginTop: theme.space[2],
}));

const FavWrapperPc = styled.div(() => ({
    [breakpoints.md]: {
        position: 'relative',
        padding: `${theme.space[2]} 0 `,
    },
    [`${FavWrapper}`]: {
        display: 'none',
        [breakpoints.md]: {
            display: 'block',
            top: 'auto',
            bottom: 0,
            padding: 0,
        },
    },
}));

const FavWrapperMobile = styled.div(() => ({
    [`${FavWrapper}`]: {
        [breakpoints.sm]: {
            top: 0,
            bottom: 'auto',
            padding: theme.space[3],
        },
        [breakpoints.md]: {
            display: 'none',
        },
    },
}));

const Description = styled.p(() => ({
    ...theme.mixins.useTextStyle('productInfo'),
}));

const Footer = styled.div(() => ({
    position: 'relative',
    marginTop: theme.space[2],
    padding: `${theme.space[4]} 0 0`,
}));

const ImageCarouselWrapper = styled.div(() => ({
    position: 'relative',
}));

const VariantPickerWrapper = styled.div<TVariantSummary>(() => ({
    padding: `${theme.space[3]} 0 ${theme.space[5]} 0`,
    [breakpoints.xs]: {
        padding: `${theme.space[2]} ${theme.space[3]}`,
    },
}));

const ToggleColor = styled.span<{ positive?: boolean }>(({ positive, theme }) => ({
    color: positive ? 'green' : 'red',
    ':after': {
        content: '" · "',
        display: 'inline-block',
        paddingLeft: theme.space[1],
        paddingRight: theme.space[1],
    },
}));

const StoreInfo = styled(({ className, name, open, closesAt }: TStoreInfo) => {
    const { translate } = useTranslation();
    const openLabel = translate('pdp.openStore');
    const closedLabel = translate('pdp.closedStore');
    return name ? (
        <div className={className}>
            <p>
                {translate(`pdp.storeInfo`, {
                    name,
                })}
            </p>
            {closesAt && openLabel && closedLabel && (
                <p>
                    <ToggleColor positive={open}>{open ? openLabel : closedLabel}</ToggleColor>
                    {translate('pdp.closesAt', { closesAt })}
                </p>
            )}
        </div>
    ) : null;
})(({ theme }) => ({
    ...theme.mixins.useTextStyle('bodySmall'),
    lineHeight: 1.4,
    padding: `${theme.space[3]} 0 0`,
}));

const StyledCampaignContainer = styled.div(({ theme }) => ({
    minHeight: theme.fontSizes.lg,
}));

const allowedCampaignRedirectTypes: ProductPromotionTypeRedirect[] = [
    'mixandmatch',
    'bastard',
    'bundle',
];

export const PDPProductCard = styled(
    (
        product: TProductDetails & {
            className?: string;
            productEventData: ProductEventData;
            showFreightBanner?: boolean;
            carrierPrice?: number;
            isLoading?: boolean;
            campaignLifeCycle?: TCampaignLifeCycle;
            variantSummary: TVariantSummary;
            yotpoAverageRating?: number;
            yotpoReviewCount?: number;
            yotpoReviewRef?: React.RefObject<HTMLDivElement>;
            c_ItemSoleRepresentation?: boolean;
        },
    ) => {
        const {
            isFavourite,
            renderShopsLink,
            storeInfo,
            name,
            longName,
            brand,
            splash,
            notifications,
            prices,
            onFavourite,
            id,
            onBuyBtn,
            onSecondaryBtn,
            canBePersonalized,
            cust_in_stock,
            cust_synopsis,
            className,
            images,
            videos,
            selectVariant,
            variationAttributes,
            toggleGallery,
            url,
            potentialBonusEarning,
            productEventData,
            showFreightBanner,
            carrierPrice,
            ctaButtons,
            isLoading,
            campaignLifeCycle,
            variantSummary,
            yotpoReviewRef,
            yotpoAverageRating,
            yotpoReviewCount,
            cust_promotionType,
        } = product;
        const { toggleMiniBasket, isInitialized } = useBasket();
        const { translate } = useTranslation();
        const { data: frame } = useFrame();
        const { labelsTopLeft, labelsTopRight } = useProductLabels(product);
        const { setToTopButtonOffset } = useMenuScroll();
        const { list } = useEvents(null);
        const isMobile = useIsMobile({ width: breakpointsRaw.sm });
        const { shouldAllowPersonalization } = useProductPersonalization(product);
        const ctaWrapperRef = useRef<HTMLDivElement>(null);
        const defaultPriceLabel = translate('pdp.defaultPriceLabel');
        const isAllowedCampaignRedirectType =
            allowedCampaignRedirectTypes.includes(cust_promotionType);
        const textCondition: string = ctaButtons.primary.translationKey;

        const canPersonalizeProduct =
            canBePersonalized && shouldAllowPersonalization && cust_in_stock;

        const notifyWhenBackDetails = useMemo(() => {
            switch (textCondition) {
                case 'comingsoon':
                case 'comingsoononline':
                    return {
                        CTAText: translate('pdp.notifyComingSoon'),
                        outOfStockText: translate('pdp.comingsoononlineBanner'),
                    };
                default:
                    return {
                        CTAText: translate('pdp.notifyDefault'),
                        outOfStockText: translate('pdp.outOfStockOnlineBanner'),
                    };
            }
        }, [textCondition]);

        const handleBuyBtn = () => {
            if (onBuyBtn && ctaButtons?.primary?.translationKey) {
                const payload = {
                    productId: id,
                    orderable: cust_in_stock,
                    url,
                };
                onBuyBtn(list, {
                    type: ctaButtons?.primary?.translationKey,
                    productEventData,
                    payload,
                    quantity: 1,
                });
            }
            toggleMiniBasket(true);
        };

        const handleSecondaryBtn = () => {
            if (onSecondaryBtn && ctaButtons?.secondary?.translationKey) {
                const payload = {
                    productId: id,
                    orderable: cust_in_stock,
                    url,
                };
                onSecondaryBtn({
                    type: ctaButtons?.secondary?.translationKey,
                    productEventData,
                    payload,
                    quantity: 1,
                });
            }
        };

        const isNotPermanentlyOutOfStock =
            textCondition !== 'outofstockpermanently' &&
            textCondition !== 'outofstockpermanentlyonline';

        useEffect(() => {
            if (!ctaWrapperRef?.current) {
                return;
            }
            ctaWrapperRef.current.clientHeight;
            setToTopButtonOffset(ctaWrapperRef.current.clientHeight);

            return () => {
                setToTopButtonOffset(null);
            };
        }, [ctaWrapperRef]);

        return (
            <Wrapper className={className}>
                <Flex column grow>
                    <TopInfo left={labelsTopLeft} right={labelsTopRight} />

                    <ImageCarouselWrapper>
                        {isMobile ? (
                            <>
                                <MobilePDPCarousel
                                    videos={videos}
                                    images={images}
                                    onClick={toggleGallery}
                                />
                                {splash &&
                                !!product.cust_promotionId &&
                                !!frame?.settings?.pageReferences.campaignPage?.url ? (
                                    <Splash variant={splash.variant}>{splash.text}</Splash>
                                ) : null}
                            </>
                        ) : null}
                        {onFavourite && (
                            <FavWrapperMobile>
                                <Favourite
                                    onFavourite={() => onFavourite(product)}
                                    isFavourite={isFavourite}
                                />
                            </FavWrapperMobile>
                        )}
                    </ImageCarouselWrapper>
                    {isMobile && (
                        <VariantPickerWrapper {...variantSummary}>
                            <PDPVariants
                                onClick={selectVariant}
                                variations={variationAttributes}
                                isLoading={isLoading}
                            />
                        </VariantPickerWrapper>
                    )}
                    <Flex flex style={{ position: 'relative' }}>
                        {splash &&
                        !isMobile &&
                        !!product.cust_promotionId &&
                        !!frame?.settings?.pageReferences.campaignPage?.url ? (
                            <Splash variant={splash.variant}>{splash.text}</Splash>
                        ) : null}

                        <Details>
                            <Flex column>
                                {yotpoAverageRating ? (
                                    <Rating
                                        rating={yotpoAverageRating}
                                        totalRatings={yotpoReviewCount}
                                        scrollToRef={yotpoReviewRef}
                                    />
                                ) : null}
                                <Name title={longName}>
                                    {brand ? <Label>{brand.text} </Label> : null}
                                    {name}
                                </Name>

                                <Description title={cust_synopsis} children={cust_synopsis} />

                                {potentialBonusEarning && (
                                    <Description
                                        title={potentialBonusEarning}
                                        children={potentialBonusEarning}
                                    />
                                )}
                            </Flex>
                            <Flex column>
                                {isAllowedCampaignRedirectType && !!product.cust_promotionId ? (
                                    <div style={{ marginTop: theme.space[3] }}>
                                        <PriceLabel align="center">{defaultPriceLabel}</PriceLabel>
                                        {prices && <Price>{prices[0].value}</Price>}
                                    </div>
                                ) : (
                                    prices && (
                                        <Prices defaultLabel={defaultPriceLabel} prices={prices} />
                                    )
                                )}

                                <Footer>
                                    {notifications && notifications.length > 0 && (
                                        <Notifications
                                            notifications={notifications}
                                            isCampaignRedirectAvailable={
                                                isAllowedCampaignRedirectType &&
                                                !!product.cust_promotionId
                                            }
                                        />
                                    )}
                                    <StyledCampaignContainer>
                                        {campaignLifeCycle && (
                                            <CampaignLifeCycle {...campaignLifeCycle} />
                                        )}
                                    </StyledCampaignContainer>
                                    {onFavourite && (
                                        <FavWrapperPc>
                                            <Favourite
                                                onFavourite={() => onFavourite(product)}
                                                isFavourite={isFavourite}
                                            />
                                        </FavWrapperPc>
                                    )}

                                    {isAllowedCampaignRedirectType &&
                                        !!product.cust_promotionId && (
                                            <CampaignRedirecter
                                                promotionId={product.cust_promotionId}
                                                campaignText={splash?.text}
                                                campaignType={cust_promotionType}
                                            />
                                        )}

                                    {!isMobile &&
                                        (variantSummary.supplierColorCount > 1 ||
                                            (variationAttributes?.length &&
                                                variationAttributes?.length > 1)) && (
                                            <VariantPickerWrapper {...variantSummary}>
                                                <PDPVariants
                                                    onClick={selectVariant}
                                                    variations={variationAttributes}
                                                    isLoading={isLoading}
                                                />
                                            </VariantPickerWrapper>
                                        )}

                                    {ctaButtons?.primary.isDisabled &&
                                        !isMobile &&
                                        isNotPermanentlyOutOfStock && (
                                            <StyledOutOfStock>
                                                {notifyWhenBackDetails.outOfStockText}
                                            </StyledOutOfStock>
                                        )}

                                    <FixedCTAWrapper ref={ctaWrapperRef}>
                                        <CTAWrapper>
                                            {ctaButtons?.primary?.shouldRender &&
                                            !ctaButtons?.primary.isDisabled ? (
                                                <CTA
                                                    variant="pdp"
                                                    disabled={
                                                        ctaButtons?.primary.isDisabled ||
                                                        isLoading ||
                                                        !isInitialized
                                                    }
                                                    text={translate(
                                                        `plp.${ctaButtons.primary.translationKey}` as TranslationKey,
                                                    )}
                                                    onClick={handleBuyBtn}
                                                    className={
                                                        !isInitialized
                                                            ? 'opacity-50 cursor-not-allowed'
                                                            : ''
                                                    }
                                                />
                                            ) : (
                                                isNotPermanentlyOutOfStock && (
                                                    <NotifyWhenBack
                                                        CTAText={notifyWhenBackDetails.CTAText}
                                                        productId={id}
                                                    />
                                                )
                                            )}

                                            {canPersonalizeProduct && (
                                                <Personalization
                                                    product={product}
                                                    eventData={productEventData}
                                                    orderOnMobile={-1}
                                                />
                                            )}

                                            {ctaButtons?.secondary?.shouldRender && (
                                                <CTA
                                                    variant="pdp-secondary"
                                                    disabled={
                                                        ctaButtons?.secondary?.isDisabled ||
                                                        isLoading ||
                                                        !renderShopsLink ||
                                                        !isInitialized
                                                    }
                                                    text={translate(
                                                        `plp.${ctaButtons?.secondary?.translationKey}` as TranslationKey,
                                                    )}
                                                    orderOnMobile={
                                                        canPersonalizeProduct ? -1 : undefined
                                                    }
                                                    onClick={handleSecondaryBtn}
                                                />
                                            )}
                                        </CTAWrapper>

                                        {ctaButtons?.primary.isDisabled &&
                                            isMobile &&
                                            isNotPermanentlyOutOfStock && (
                                                <StyledOutOfStock>
                                                    {notifyWhenBackDetails.outOfStockText}
                                                </StyledOutOfStock>
                                            )}
                                    </FixedCTAWrapper>

                                    <Spacer space="16px" />

                                    <StoreInfo {...storeInfo} />
                                    {renderShopsLink}
                                </Footer>
                            </Flex>
                        </Details>
                    </Flex>
                    {showFreightBanner && <Freight cost={carrierPrice} />}
                </Flex>
            </Wrapper>
        );
    },
)(({ theme }) => ({
    [`${StyledAspectRatio}`]: {
        display: 'block',
    },
    [`${InfoWrapper}`]: {
        padding: theme.space[3],
        [breakpoints.sm]: {
            position: 'relative',
        },

        [breakpoints.xs]: {
            fontSize: theme.fontSizes.xs,
            lineHeight: theme.lineHeights.base,
        },
    },

    [`${PositionSplash}`]: {
        [breakpoints.xs]: {
            bottom: '45px',
            top: 'auto',

            [`${StyledSplash}`]: {
                zIndex: 1,
                padding: theme.space[2],
                fontSize: theme.fontSizes.xs,
            },
        },
    },
    [`${Details}`]: {
        padding: theme.space[3],
        [`${PriceLabel}`]: {
            lineHeight: theme.lineHeights.base,
        },
        [`${NotificationLabel}`]: {
            lineHeight: 1,
        },
        [breakpoints.xs]: {
            [`${Label}`]: {
                fontSize: theme.fontSizes.xs,
                lineHeight: theme.lineHeights.compact,
                marginTop: 0,
            },
            [`${PriceLabel}`]: {
                fontSize: theme.fontSizes.xs,
                lineHeight: theme.lineHeights.base,
            },
            [`${Description}`]: {
                fontSize: theme.fontSizes.xs,
                lineHeight: theme.lineHeights.base,
            },
            [`${Name}`]: {
                fontSize: theme.fontSizes.md,
                lineHeight: theme.lineHeights.base,
            },
            [`${Price}`]: {
                fontSize: '20px',
                lineHeight: theme.lineHeights.compact,
            },

            [`${NotificationWrap}`]: {
                top: '-5px',
            },
            [`${NotificationLabel}`]: {
                fontSize: theme.fontSizes.xs,
                lineHeight: theme.lineHeights.base,
            },
            [`${Footer}`]: {
                paddingTop: theme.space[6],
            },
        },
    },
}));
